import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import api from './api';

const KATEGORI_ETIKETLER = {
  yukseltme_talebi: { label: 'Hesap Yükseltme', renk: 'warning' },
  teknik_sorun:     { label: 'Teknik Sorun',    renk: 'danger'  },
  fatura:           { label: 'Fatura',           renk: 'info'    },
  diger:            { label: 'Diğer',            renk: 'secondary' },
};

const DURUM_ETIKETLER = {
  acik:       { label: 'Açık',       renk: 'success' },
  beklemede:  { label: 'Beklemede',  renk: 'warning' },
  cozuldu:    { label: 'Çözüldü',    renk: 'primary' },
  reddedildi: { label: 'Reddedildi', renk: 'danger'  },
};

function TicketDetay({ theme }) {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket]         = useState(null);
  const [mesajlar, setMesajlar]     = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata]             = useState(null);
  const [yeniMesaj, setYeniMesaj]   = useState('');
  const [gonderiyor, setGonderiyor] = useState(false);
  const [gonderHata, setGonderHata] = useState(null);
  const [sseHata, setSseHata]       = useState(false);

  const mesajlarSonuRef = useRef(null);
  const sseAktifRef     = useRef(false);

  const isDark = theme === 'dark';
  const kapali = ticket && ['cozuldu', 'reddedildi'].includes(ticket.durum);

  const scrollToBottom = () =>
    mesajlarSonuRef.current?.scrollIntoView({ behavior: 'smooth' });

  /* ── Ticket yükle ─────────────────────────────────────────── */
  const fetchTicket = useCallback(async () => {
    try {
      const res = await api.get(`/diyetisyen/destek/ticket/${ticketId}`);
      setTicket(res.data);
      setMesajlar(res.data.mesajlar || []);
    } catch (e) {
      setHata(e.response?.status === 404
        ? 'Ticket bulunamadı.'
        : 'Ticket yüklenirken hata oluştu.');
    } finally {
      setYukleniyor(false);
    }
  }, [ticketId]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);
  useEffect(() => { scrollToBottom(); }, [mesajlar]);

  /* ── SSE ──────────────────────────────────────────────────── */
  useEffect(() => {
    if (yukleniyor || hata || kapali) return;
    sseAktifRef.current = true;
    const token = window._accessToken || localStorage.getItem('accessToken');
    if (!token) return;

    const url = `${process.env.REACT_APP_API_URL}/diyetisyen/destek/ticket/${ticketId}/stream`;

    const connect = async () => {
      try {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!response.ok) { setSseHata(true); return; }

        const reader  = response.body.getReader();
        const decoder = new TextDecoder();

        while (sseAktifRef.current) {
          const { value, done } = await reader.read();
          if (done) break;
          const lines = decoder.decode(value, { stream: true }).split('\n');
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const mesaj = JSON.parse(line.slice(6));
              setMesajlar(prev =>
                prev.find(m => m.id === mesaj.id) ? prev : [...prev, mesaj]
              );
            } catch { /* JSON parse hatası */ }
          }
        }
      } catch {
        if (sseAktifRef.current) setTimeout(connect, 3000);
      }
    };

    connect();
    return () => { sseAktifRef.current = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yukleniyor, hata, kapali, ticketId]);

  /* ── Mesaj gönder ─────────────────────────────────────────── */
  const handleMesajGonder = async (e) => {
    e.preventDefault();
    const icerik = yeniMesaj.trim();
    if (!icerik) return;
    setGonderiyor(true);
    setGonderHata(null);
    try {
      const res = await api.post(
        `/diyetisyen/destek/ticket/${ticketId}/mesaj`,
        { icerik }
      );
      setMesajlar(prev =>
        prev.find(m => m.id === res.data.id) ? prev : [...prev, res.data]
      );
      setYeniMesaj('');
    } catch (err) {
      setGonderHata(err.response?.data?.mesaj || 'Mesaj gönderilemedi.');
    } finally {
      setGonderiyor(false);
    }
  };

  /* ── Render ───────────────────────────────────────────────── */
  if (yukleniyor) return (
    <Container fluid className="py-5 text-center">
      <Spinner animation="border" variant="primary" />
    </Container>
  );

  if (hata) return (
    <Container fluid className="py-4">
      <Alert variant="danger">{hata}</Alert>
      <Button variant="outline-secondary" onClick={() => navigate('/destek')}>
        <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: '1rem' }}>arrow_back</span>
        Geri
      </Button>
    </Container>
  );

  const kat = KATEGORI_ETIKETLER[ticket.kategori] ?? KATEGORI_ETIKETLER.diger;
  const dur = DURUM_ETIKETLER[ticket.durum]       ?? DURUM_ETIKETLER.acik;

  return (
    <Container fluid className="py-4 px-3 px-md-4" style={{ maxWidth: 800 }}>

      {/* ── Üst bar ── */}
      <div className="d-flex align-items-center gap-2 mb-4">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/destek')}>
          <span className="material-symbols-outlined align-middle" style={{ fontSize: '1.1rem' }}>arrow_back</span>
        </Button>
        <div className="flex-grow-1 min-w-0">
          <h5 className="fw-bold mb-0 text-truncate">{ticket.baslik}</h5>
          <div className="d-flex gap-2 align-items-center mt-1">
            <Badge bg={kat.renk} className="text-dark">{kat.label}</Badge>
            <Badge bg={dur.renk}>{dur.label}</Badge>
            <span className="text-muted small">#{ticket.id}</span>
          </div>
        </div>
      </div>

      {sseHata && (
        <Alert variant="warning" className="py-2 small mb-3">
          <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: '1rem' }}>wifi_off</span>
          Gerçek zamanlı bağlantı kurulamadı. Sayfayı yenileyerek mesajları görebilirsiniz.
        </Alert>
      )}

      {/* ── Mesaj listesi ── */}
      <div
        className="border rounded-3 p-3 mb-3"
        style={{
          maxHeight: 500,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          backgroundColor: 'var(--bg-secondary)',
        }}
      >
        {mesajlar.length === 0
          ? <p className="text-muted text-center my-4">Henüz mesaj yok.</p>
          : mesajlar.map(mesaj => {
              const benimMesajim = mesaj.gonderen_rol !== 'admin';
              return (
                <div key={mesaj.id} className={`d-flex ${benimMesajim ? 'justify-content-end' : 'justify-content-start'}`}>
                  <div
                    className={`px-3 py-2 rounded-3 ${benimMesajim
                      ? 'bg-primary text-white'
                      : isDark ? 'bg-secondary text-white' : 'bg-light text-dark'
                    }`}
                    style={{ maxWidth: '75%', wordBreak: 'break-word' }}
                  >
                    {!benimMesajim && (
                      <div className="fw-bold small mb-1 opacity-75">
                        <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: '0.9rem' }}>support_agent</span>
                        Destek Ekibi
                      </div>
                    )}
                    <div>{mesaj.icerik}</div>
                    <div className={`small mt-1 ${benimMesajim ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '0.7rem' }}>
                      {new Date(mesaj.tarih).toLocaleString('tr-TR', {
                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              );
            })
        }
        <div ref={mesajlarSonuRef} />
      </div>

      {/* ── Kapalı bildirim / Mesaj formu ── */}
      {kapali ? (
        <Alert variant={ticket.durum === 'cozuldu' ? 'success' : 'secondary'} className="text-center mb-0">
          <span className="material-symbols-outlined align-middle me-2">
            {ticket.durum === 'cozuldu' ? 'check_circle' : 'cancel'}
          </span>
          Bu ticket {ticket.durum === 'cozuldu' ? 'çözüldü' : 'reddedildi'}.
          <div className="mt-2">
            <Button variant="outline-primary" size="sm" onClick={() => navigate('/destek')}>
              Taleplerime Dön
            </Button>
          </div>
        </Alert>
      ) : (
        <Form onSubmit={handleMesajGonder}>
          {gonderHata && <Alert variant="danger" className="py-2 small mb-2">{gonderHata}</Alert>}
          <div className="d-flex gap-2">
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Mesajınızı yazın..."
              value={yeniMesaj}
              onChange={e => setYeniMesaj(e.target.value)}
              disabled={gonderiyor}
              className={isDark ? 'bg-secondary text-white border-secondary' : ''}
              onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleMesajGonder(e); }}
            />
            <Button
              type="submit"
              variant="primary"
              disabled={gonderiyor || !yeniMesaj.trim()}
              style={{ alignSelf: 'flex-end', minWidth: 46 }}
            >
              {gonderiyor
                ? <Spinner size="sm" animation="border" />
                : <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>send</span>
              }
            </Button>
          </div>
          <div className="text-muted small mt-1">Ctrl+Enter ile hızlı gönder</div>
        </Form>
      )}
    </Container>
  );
}

export default TicketDetay;
